import { useCallback } from 'react';
import { useApi, fetchApiRef } from '@backstage/frontend-plugin-api';
import { checkResultsResponseSchema } from '../schemas/checks';
import { toPercentageDecimal } from '@internal/backstage-plugin-pulse-common';
import {
  PULSE_CACHE_KEYS,
  PULSE_QUERY_CACHE_POLICIES,
  useCachedQuery,
} from './cache';

/**
 * Aggregated pass/fail counts for a single check across every entity the
 * check applies to. Used to render the "Checks By Levels" breakdown.
 */
export type TrackCheckAggregate = {
  checkId: string;
  name: string;
  passedEntities: number;
  totalEntities: number;
  percentage: number;
};

/**
 * Fetches the latest results for each of the given check ids and aggregates
 * them into per-check pass/fail counts. When `entityRefs` is provided, the
 * aggregation is limited to those entities so the breakdown reflects the
 * currently filtered set.
 */
export const useTrackChecks = (checkIds: string[], entityRefs?: string[]) => {
  const fetchApi = useApi(fetchApiRef);

  const fetchTrackChecks = useCallback(async () => {
    const uniqueCheckIds = [...new Set(checkIds)];
    const selectedEntityRefs =
      entityRefs === undefined ? undefined : new Set(entityRefs);

    const entries = await Promise.all(
      uniqueCheckIds.map(async checkId => {
        const response = await fetchApi.fetch(
          `plugin://pulse/checks/${encodeURIComponent(checkId)}`,
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch check "${checkId}": ${response.status} ${response.statusText}`,
          );
        }

        const data = await response.json();
        const parsed = checkResultsResponseSchema.parse(data);
        const scopedResults = selectedEntityRefs
          ? parsed.results.filter(result =>
              selectedEntityRefs.has(result.entityRef),
            )
          : parsed.results;
        const totalEntities = scopedResults.length;
        const passedEntities = scopedResults.filter(
          result => result.checkResult.passed,
        ).length;
        const percentage =
          totalEntities === 0
            ? 0
            : toPercentageDecimal(passedEntities, totalEntities);

        return [
          checkId,
          {
            checkId,
            name: parsed.checkName || checkId,
            passedEntities,
            totalEntities,
            percentage,
          },
        ] as const;
      }),
    );

    return new Map<string, TrackCheckAggregate>(entries);
  }, [checkIds, entityRefs, fetchApi]);

  return useCachedQuery({
    cacheKey: PULSE_CACHE_KEYS.trackChecks(checkIds, entityRefs),
    fetcher: fetchTrackChecks,
    ...PULSE_QUERY_CACHE_POLICIES.details,
    enabled: checkIds.length > 0,
  });
};
